import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { articles } from '@/mocks/articles';
import { comics } from '@/mocks/comics';
import { testimonials } from '@/mocks/testimonials';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id)) || articles[0];
  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);
  const relatedComics = comics.slice(0, 3);

  return (
    <>
      <Header />
      <main>
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 pt-24 pb-0 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                  首页
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li>
                <Link to="/article/1" className="hover:text-primary-500 transition-colors cursor-pointer">
                  资讯中心
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </div>
        </nav>

        {/* Article Hero */}
        <article className="w-full px-4 md:px-6 pt-8 pb-6 bg-background-50" aria-labelledby="article-title">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-full mb-4">
                  {article.category}
                </span>
                <h1 id="article-title" className="text-2xl md:text-4xl font-heading text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views} 阅读
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-center gap-4">
                <p className="text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-bookmark-line"></i>
                    收藏文章
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-foreground-700 bg-background-100 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Article Cover */}
        <div className="w-full px-4 md:px-6 pb-10 bg-background-50">
          <div className="max-w-[1400px] mx-auto">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full rounded-lg object-cover object-top max-h-[400px]"
            />
          </div>
        </div>

        {/* Article Content */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-[900px] mx-auto">
            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed [&>h2]:text-xl [&>h2]:font-heading [&>h2]:text-foreground-950 [&>h2]:mt-8 [&>h2]:mb-4 [&>p]:mb-5 [&>p]:text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </section>

        {/* Related Comics */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50" aria-labelledby="related-comics-title">
          <div className="max-w-[1400px] mx-auto">
            <h2 id="related-comics-title" className="text-xl md:text-2xl font-heading text-foreground-950 mb-8 text-center">
              相关漫画推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {relatedComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} variant="compact" />
              ))}
            </div>
          </div>
        </section>

        {/* Reader Comments */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100" aria-labelledby="comments-title">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-10">
              <h2 id="comments-title" className="text-xl md:text-2xl font-heading text-foreground-950">
                读者评论
              </h2>
              <p className="mt-2 text-sm text-foreground-600">看看其他漫友怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {testimonials.slice(0, 6).map((item) => (
                <div key={item.id} className="p-5 bg-background-50 rounded-lg">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{item.content}</p>
                  <div className="flex items-center gap-2.5">
                    <img src={item.avatar} alt={item.name} className="w-8 h-8 rounded-full object-cover" />
                    <span className="text-sm font-medium text-foreground-900">{item.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50" aria-labelledby="related-articles-title">
          <div className="max-w-[1400px] mx-auto">
            <h2 id="related-articles-title" className="text-xl md:text-2xl font-heading text-foreground-950 mb-8 text-center">
              更多精彩文章
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((a) => (
                <Link
                  key={a.id}
                  to={`/article/${a.id}`}
                  className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-background-200">
                    <img
                      src={a.cover}
                      alt={a.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-primary-500 font-medium">{a.category}</span>
                    <h3 className="mt-1 text-sm font-medium text-foreground-900 line-clamp-2">{a.title}</h3>
                    <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                      <span>{a.date}</span>
                      <span>{a.views} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.cover,
            publisher: {
              '@type': 'Organization',
              name: '虫虫漫画',
            },
          }),
        }}
      />
      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.chongchongcomic.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '资讯中心',
                item: 'https://www.chongchongcomic.com/article/1',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}